import { useEffect, useRef, useState, useCallback } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";

const PHOTON_BASE = "https://photon.komoot.io/api";
const GOOGLE_SCRIPT_ID = "google-maps-places-js";

function loadGoogleMapsScript(apiKey) {
    return new Promise((resolve, reject) => {
        if (window.google?.maps?.places) {
            resolve();
            return;
        }
        const existing = document.getElementById(GOOGLE_SCRIPT_ID);
        if (existing) {
            const done = () => resolve();
            const fail = () => reject(new Error("Google Maps failed to load"));
            if (window.google?.maps?.places) {
                resolve();
                return;
            }
            existing.addEventListener("load", done, { once: true });
            existing.addEventListener("error", fail, { once: true });
            return;
        }
        const script = document.createElement("script");
        script.id = GOOGLE_SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Google Maps failed to load"));
        document.head.appendChild(script);
    });
}

function buildPhotonLabel(feature) {
    const p = feature?.properties || {};
    const parts = [
        p.name,
        p.street,
        p.district,
        p.city || p.town || p.village,
        p.state,
        p.country,
    ].filter(Boolean);
    const unique = [...new Set(parts)];
    return unique.join(", ") || p.display_name || "Selected location";
}

/**
 * Delivery location search (Rapido-style): pick from list → address + lat/lng.
 * Uses Google Places when VITE_GOOGLE_MAPS_API_KEY is set; otherwise Photon (no key).
 */
export default function DeliveryLocationSearch({
    value = "",
    /** Prefer API-shaped props (matches backend: delivery_landmark, latitude, longitude). */
    latitude,
    longitude,
    /** Legacy aliases */
    lat,
    lng,
    onChange,
    disabled = false,
    placeholder = "Search delivery location…",
    error,
    inputClassName = "",
}) {
    const latVal = latitude ?? lat;
    const lngVal = longitude ?? lng;
    const googleKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    const [text, setText] = useState(value);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [googleReady, setGoogleReady] = useState(false);
    const [googleLoadError, setGoogleLoadError] = useState(false);
    const [googleSearchError, setGoogleSearchError] = useState(false);

    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    /** Hidden node required by `PlacesService` (no visible map needed). */
    const placesAttributionRef = useRef(null);

    useEffect(() => {
        setText(value || "");
    }, [value]);

    useEffect(() => {
        if (!googleKey) {
            setGoogleReady(false);
            return;
        }
        let cancelled = false;
        loadGoogleMapsScript(googleKey)
            .then(() => {
                if (!cancelled) {
                    setGoogleReady(true);
                    setGoogleLoadError(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setGoogleReady(false);
                    setGoogleLoadError(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [googleKey]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const runGoogleSearch = useCallback((q) => {
        if (!q || q.trim().length < 2 || !window.google?.maps?.places) {
            setSuggestions([]);
            return;
        }
        setLoading(true);
        const ac = new window.google.maps.places.AutocompleteService();
        ac.getPlacePredictions(
            {
                input: q.trim(),
                types: ["geocode", "establishment"],
            },
            (predictions, status) => {
                setLoading(false);
                const Status = window.google.maps.places.PlacesServiceStatus;
                if (status === Status.ZERO_RESULTS) {
                setGoogleSearchError(false);
                setSuggestions([]);
                return;
            }
            if (status !== Status.OK || !predictions?.length) {
                setGoogleSearchError(true);
                setSuggestions([]);
                return;
            }
            setGoogleSearchError(false);
                setSuggestions(
                    predictions.map((p) => ({
                        id: p.place_id,
                        label: p.description,
                        place_id: p.place_id,
                        source: "google",
                    }))
                );
            }
        );
    }, []);

    const runPhotonSearch = useCallback(async (q) => {
        if (!q || q.trim().length < 2) {
            setSuggestions([]);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(
                `${PHOTON_BASE}/?q=${encodeURIComponent(q.trim())}&limit=8`
            );
            if (!res.ok) throw new Error("search failed");
            const data = await res.json();
            const list = (data.features || []).map((f) => {
                const coords = f.geometry?.coordinates || [];
                const lngVal = coords[0];
                const latVal = coords[1];
                return {
                    id: `${latVal},${lngVal},${buildPhotonLabel(f)}`,
                    label: buildPhotonLabel(f),
                    lat: latVal,
                    lng: lngVal,
                    source: "photon",
                };
            });
            setSuggestions(list);
        } catch {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const scheduleSearch = useCallback(
        (q) => {
            if (googleKey && googleReady) {
                runGoogleSearch(q);
            } else if (!googleKey) {
                runPhotonSearch(q);
            } else {
                setSuggestions([]);
            }
        },
        [googleKey, googleReady, runGoogleSearch, runPhotonSearch]
    );

    const handleInputChange = (e) => {
        const v = e.target.value;
       
        setText(v);
        setOpen(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => scheduleSearch(v), 320);
        onChange?.({
            delivery_landmark: v,
            pickup_landmark: v,
            delivery_address:v,
            pickup_location:v,
            latitude: null,
            longitude: null,
        });
    };

    const pickPhoton = (item) => {
        setText(item.label);
        setOpen(false);
        setSuggestions([]);
        onChange?.({
            delivery_landmark: item.label,
            pickup_landmark: item.label,
            delivery_address:item.label,
            pickup_location:item.label,
            latitude: item.lat,
            longitude: item.lng,
        });
    };

    const pickGoogle = (item) => {
        if (!placesAttributionRef.current || !window.google?.maps?.places) {
            return;
        }
        setLoading(true);
        const service = new window.google.maps.places.PlacesService(
            placesAttributionRef.current
        );
        service.getDetails(
            {
                placeId: item.place_id,
                fields: ["geometry", "formatted_address", "name"],
            },
            (place, status) => {
                setLoading(false);
                setOpen(false);
                setSuggestions([]);
                if (
                    status === window.google.maps.places.PlacesServiceStatus.OK &&
                    place?.geometry?.location
                ) {
                    const addr =
                        place.formatted_address || place.name || item.label;
                    setText(addr);
                    onChange?.({
                        delivery_landmark: addr,
                        pickup_landmark: addr,
                        delivery_address:addr,
                        pickup_location:addr,
                        latitude: place.geometry.location.lat(),
                        longitude: place.geometry.location.lng(),
                    });
                }
            }
        );
    };

    const pickSuggestion = (item) => {
        if (item.source === "google") {
            pickGoogle(item);
        } else {
            pickPhoton(item);
        }
    };

    const hasCoords =
        latVal != null &&
        lngVal != null &&
        !Number.isNaN(Number(latVal)) &&
        !Number.isNaN(Number(lngVal));

    const showGoogleHint =
        googleKey && !googleReady && !googleLoadError && !disabled;
    const hasGoogleFailed = Boolean(googleKey) && (googleLoadError || googleSearchError);

    return (
        <div ref={wrapperRef} className="relative">
            {/* Google PlacesService requires a map-like node (can be hidden). */}
            <div
                ref={placesAttributionRef}
                className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
                aria-hidden
            />

            <div className="relative">
                <MapPinIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
                <input
                    type="text"
                    value={text}
                    onChange={handleInputChange}
                    onFocus={() => {
                        if (text.trim().length >= 2) setOpen(true);
                    }}
                    disabled={disabled}
                    placeholder={
                        googleKey && !googleReady
                            ? "Loading Google Places…"
                            : placeholder
                    }
                    autoComplete="off"
                    className={`w-full rounded-lg border border-[#CFD2D9] py-3 pl-10 pr-3 text-sm text-[#111827] outline-none transition focus:border-[#212121] ${inputClassName}`}
                />
                {loading && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                        …
                    </span>
                )}
            </div>

            {showGoogleHint && (
                <p className="mt-1 text-xs text-gray-500">
                    Loading Google Places…
                </p>
            )}
            {/* {hasGoogleFailed && (
                <p className="mt-1 text-xs text-amber-700">
                    Google Maps could not load. 
                </p>
            )} */}
            {!googleKey && (
                <p className="mt-1 text-xs text-gray-500">
                    Add{" "}
                    <code className="rounded bg-gray-100 px-1">
                        VITE_GOOGLE_MAPS_API_KEY
                    </code>{" "}
                    in <code className="rounded bg-gray-100 px-1">.env</code> to use
                    Google Places (fallback search is active until then).
                </p>
            )}

            {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
            {hasCoords && (
                <p className="mt-1.5 text-xs text-gray-500">
                    Lat {Number(latVal).toFixed(6)}, Lng {Number(lngVal).toFixed(6)}
                </p>
            )}

            {open && suggestions.length > 0 && (
                <ul className="absolute z-[70] mt-1 max-h-56 w-full overflow-auto rounded-lg border border-[#E5E7EB] bg-white py-1 shadow-lg">
                    {suggestions.map((s) => (
                        <li
                            key={s.id}
                            role="button"
                            tabIndex={0}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickSuggestion(s)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") pickSuggestion(s);
                            }}
                            className="cursor-pointer border-b border-gray-50 px-3 py-2.5 text-left text-sm text-gray-800 last:border-0 hover:bg-gray-50"
                        >
                            {s.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
